import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useStore } from '../context/StoreContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Store as StoreIcon,
  MapPin,
  Mail,
  Phone,
  Building2,
  Building,
  Fuel,
  Package,
  CreditCard,
  Loader2,
  ChevronRight,
  Sparkles,
  Wand2,
  AlertTriangle,
  ShieldAlert,
  CheckCircle,
  Coffee,
  Wallet,
  ArrowRight,
  Landmark,
  Check,
  X
} from 'lucide-react';
import StoreSetupWizard from '../components/StoreSetupWizard';
import { getFuelGradeColor } from '../utils/fuelGradeColors';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TILL_SYSTEMS = [
  { value: 'oyslans', label: 'Oyslans' },
  { value: 'iposg', label: 'IPOSG' },
  { value: 'iposg_bo', label: 'IPOSG Back Office' },
  { value: 'vbr', label: 'VBR' },
];

const STORE_TYPES = [
  { value: 'convenience_store', label: 'Convenience Store' },
  { value: 'fuel_station', label: 'Fuel Station' },
  { value: 'property_rental', label: 'Property Rental' }
];

export const StoresPage = () => {
  const { isAdmin } = useAuth();
  const { stores, fetchStores } = useStore();
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [detailStore, setDetailStore] = useState(null);
  const [activeTab, setActiveTab] = useState('info');
  const [formData, setFormData] = useState({
    parent_company: '',
    name: '',
    location: '',
    email: '',
    phone: '',
    store_type: 'convenience_store',
    bank_account_name: '',
    departments: [],
    payment_types: [],
    services: [],
    enabled_services: [],
    fuel_grades: [],
    fuel_cards_enabled: true,
    amex_tracking_enabled: false,
    till_system: '',
    voucher_tracking_enabled: false,
    creditor_request_enabled: false,
    active_fuel_cards: [],
    email_secondary: '',
    // Department Monitoring for Purchases
    department_monitoring_enabled: false,
    monitored_departments: [],  // [{department_id, department_name, vat_rate}]
    // Coffee Tracking
    coffee_tracking: {
      coffee_sold_enabled: false,
      small_enabled: false,
      large_enabled: false,
      small_opening_reading: 0,
      large_opening_reading: 0
    },
    // Opening Balances
    opening_balances: {
      cash_holding: 0,
      safe: 0,
      creditors: 0,
      investment_scheme: 0
    },
    // Multiple Bank Accounts
    bank_accounts: [],
    // Multiple Credit Cards
    credit_cards: [],
    // Payment Routing
    payment_routing: {
      card_payments: null,
      card_payments_bms: null,
      bunkering: null,
      non_bunkering: null,
      key_fuels: null
    },
    balances_locked: false
  });
  const [saving, setSaving] = useState(false);

  // Global settings from API
  const [globalServices, setGlobalServices] = useState([]);
  const [globalPaymentTypes, setGlobalPaymentTypes] = useState([]);
  const [globalFuelCardTypes, setGlobalFuelCardTypes] = useState([]);

  // Department form
  const [newDept, setNewDept] = useState({ name: '', field_type: 'amount' });
  // Fuel grade form
  const [newFuelGrade, setNewFuelGrade] = useState({ name: '', average_price: '', selling_price: '', opening_stock: '' });
  // Custom payment type
  const [newPaymentType, setNewPaymentType] = useState('');

  // Delete verification state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [storeToDelete, setStoreToDelete] = useState(null);
  const [deleteStep, setDeleteStep] = useState('confirm'); // 'confirm' | 'verify'
  const [verificationCode, setVerificationCode] = useState('');
  const [deletingStore, setDeletingStore] = useState(false);

  // Fetch global services and payment types
  const fetchGlobalSettings = async () => {
    try {
      const [servicesRes, paymentTypesRes, fuelCardTypesRes] = await Promise.all([
        axios.get(`${API}/settings/services`),
        axios.get(`${API}/settings/payment-types`),
        axios.get(`${API}/settings/fuel-card-types`)
      ]);
      setGlobalServices(servicesRes.data.filter(s => s.is_active) || []);
      setGlobalPaymentTypes(paymentTypesRes.data.filter(p => p.is_active) || []);
      setGlobalFuelCardTypes(fuelCardTypesRes.data.filter(fc => fc.is_active) || []);
    } catch (error) {
      console.error('Failed to fetch global settings:', error);
    }
  };

  useEffect(() => {
    const loadStores = async () => {
      setLoading(true);
      await Promise.all([fetchStores(), fetchGlobalSettings()]);
      setLoading(false);
    };
    loadStores();
  }, []);

  const handleOpenDialog = (store = null) => {
    if (store) {
      setEditingStore(store);
      
      // Merge store's payment types with global payment types to ensure all options are shown
      const storePaymentNames = (store.payment_types || []).map(p => p.name.toLowerCase());
      const mergedPaymentTypes = globalPaymentTypes.map((defaultPt, i) => {
        const storePt = (store.payment_types || []).find(p => p.name.toLowerCase() === defaultPt.name.toLowerCase());
        return {
          id: storePt?.id || `pt-${i}`,
          name: defaultPt.name,
          is_active: storePt ? storePt.is_active : false
        };
      });
      // Add any custom payment types from the store that aren't in globals
      (store.payment_types || []).forEach(storePt => {
        const isGlobal = globalPaymentTypes.some(d => d.name.toLowerCase() === storePt.name.toLowerCase());
        if (!isGlobal) {
          mergedPaymentTypes.push({
            id: storePt.id || `pt-custom-${Date.now()}`,
            name: storePt.name,
            is_active: storePt.is_active
          });
        }
      });
      
      setFormData({
        parent_company: store.parent_company || '',
        name: store.name,
        location: store.location,
        email: store.email,
        phone: store.phone,
        store_type: store.store_type,
        bank_account_name: store.bank_account_name || '',
        departments: store.departments || [],
        payment_types: mergedPaymentTypes,
        services: store.services || [],
        enabled_services: store.enabled_services || [],
        fuel_grades: store.fuel_grades || [],
        enable_total_sales: true, // Always required
        enable_other_departments: store.enable_other_departments || false,
        fuel_cards_enabled: store.store_type === 'fuel_station' ? true : false,
        amex_tracking_enabled: store.amex_tracking_enabled || false,
        till_system: store.till_system || '',
        voucher_tracking_enabled: store.voucher_tracking_enabled || false,
        creditor_request_enabled: store.creditor_request_enabled || false,
        active_fuel_cards: store.active_fuel_cards || [],
        email_secondary: store.email_secondary || '',
        // Department Monitoring for Purchases
        department_monitoring_enabled: store.department_monitoring_enabled || false,
        monitored_departments: store.monitored_departments || [],
        // Coffee Tracking
        coffee_tracking: store.coffee_tracking || {
          coffee_sold_enabled: false,
          small_enabled: false,
          large_enabled: false,
          small_opening_reading: 0,
          large_opening_reading: 0
        },
        // Opening Balances
        opening_balances: store.opening_balances || {
          cash_holding: 0,
          safe: 0,
          creditors: 0,
          investment_scheme: 0
        },
        // Bank Accounts & Credit Cards
        bank_accounts: store.bank_accounts || [],
        credit_cards: store.credit_cards || [],
        // Payment Routing
        payment_routing: store.payment_routing || {
          card_payments: null,
          card_payments_bms: null,
          bunkering: null,
          non_bunkering: null,
          key_fuels: null
        },
        balances_locked: store.balances_locked || false
      });
    } else {
      setEditingStore(null);
      setFormData({
        parent_company: '',
        name: '',
        location: '',
        email: '',
        phone: '',
        store_type: 'convenience_store',
        bank_account_name: '',
        departments: [],
        payment_types: globalPaymentTypes.map((p, i) => ({ ...p, id: `pt-${i}`, is_active: p.is_active !== false })),
        services: [],
        enabled_services: [],
        fuel_grades: [],
        enable_total_sales: true, // Always required
        enable_other_departments: false,
        fuel_cards_enabled: true, // Always enabled for fuel stations
        amex_tracking_enabled: false,
        till_system: '',
        voucher_tracking_enabled: false,
        creditor_request_enabled: false,
        active_fuel_cards: [],
        // Department Monitoring for Purchases
        department_monitoring_enabled: false,
        monitored_departments: [],
        // Coffee Tracking
        coffee_tracking: {
          coffee_sold_enabled: false,
          small_enabled: false,
          large_enabled: false,
          small_opening_reading: 0,
          large_opening_reading: 0
        },
        // Opening Balances
        opening_balances: {
          cash_holding: 0,
          safe: 0,
          creditors: 0,
          investment_scheme: 0
        },
        // Bank Accounts & Credit Cards
        bank_accounts: [],
        credit_cards: [],
        // Payment Routing
        payment_routing: {
          card_payments: null,
          card_payments_bms: null,
          bunkering: null,
          non_bunkering: null,
          key_fuels: null
        },
        balances_locked: false
      });
    }
    setDialogOpen(true);
    setActiveTab('info');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingStore) {
        await axios.put(`${API}/stores/${editingStore.id}`, formData);
        toast.success('Store updated successfully');
      } else {
        await axios.post(`${API}/stores`, formData);
        toast.success('Store created successfully');
      }
      setDialogOpen(false);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save store');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (store) => {
    setStoreToDelete(store);
    setDeleteStep('confirm');
    setVerificationCode('');
    setDeleteDialogOpen(true);
  };

  const handleRequestDeleteCode = async () => {
    if (!storeToDelete) return;
    
    setDeletingStore(true);
    try {
      const response = await axios.post(`${API}/stores/${storeToDelete.id}/request-delete`);
      toast.success(response.data.message || 'Verification code sent to your email');
      setDeleteStep('verify');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request deletion');
    } finally {
      setDeletingStore(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!storeToDelete || !verificationCode) return;
    
    setDeletingStore(true);
    try {
      await axios.post(`${API}/stores/${storeToDelete.id}/confirm-delete`, {
        verification_code: verificationCode
      });
      toast.success(`Store "${storeToDelete.name}" and all its data have been permanently deleted`);
      setDeleteDialogOpen(false);
      setStoreToDelete(null);
      setVerificationCode('');
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete store');
    } finally {
      setDeletingStore(false);
    }
  };

  const handleWizardComplete = async (storeData) => {
    try {
      await axios.post(`${API}/stores`, storeData);
      toast.success('Store created successfully!');
      setWizardOpen(false);
      fetchStores();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create store');
      throw error;
    }
  };

  const addDepartment = () => {
    if (!newDept.name) {
      toast.error('Department name is required');
      return;
    }
    // Check for duplicate department names
    const isDuplicate = formData.departments.some(
      d => d.name.toLowerCase() === newDept.name.toLowerCase()
    );
    if (isDuplicate) {
      toast.error('A department with this name already exists');
      return;
    }
    setFormData(prev => ({
      ...prev,
      departments: [...prev.departments, { ...newDept, id: Date.now().toString() }]
    }));
    setNewDept({ name: '', field_type: 'amount' });
  };

  // Rename keeps the department id, so historical sales stay linked
  const [editingDeptId, setEditingDeptId] = useState(null);
  const [editingDeptName, setEditingDeptName] = useState('');
  const saveDepartmentName = (deptId) => {
    const name = editingDeptName.trim();
    if (!name) {
      toast.error('Department name cannot be empty');
      return;
    }
    if (name.toLowerCase() === 'fuel sales' || formData.departments.some(d => d.id !== deptId && d.name.trim().toLowerCase() === name.toLowerCase())) {
      toast.error('A department with this name already exists');
      return;
    }
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.map(d => d.id === deptId ? { ...d, name } : d),
      monitored_departments: (prev.monitored_departments || []).map(m =>
        m.department_id === deptId ? { ...m, department_name: name } : m
      )
    }));
    setEditingDeptId(null);
    toast.info('Department renamed - click Update Store to apply');
  };

  const removeDepartment = (deptId) => {
    // Find the department to check if it's a required department
    const dept = formData.departments?.find(d => d.id === deptId);
    if (dept && dept.name === 'Fuel Sales') {
      return; // Can't delete required departments
    }
    setFormData(prev => ({
      ...prev,
      departments: prev.departments.filter(d => d.id !== deptId)
    }));
  };

  const toggleService = (serviceKey) => {
    const currentServices = formData.services || [];
    const isEnabled = currentServices.includes(serviceKey);
    
    if (isEnabled) {
      setFormData(prev => ({
        ...prev,
        services: prev.services.filter(s => s !== serviceKey),
        enabled_services: (prev.enabled_services || []).filter(s => s.key !== serviceKey)
      }));
    } else {
      // Find service info from globalServices (fetched from API)
      const serviceInfo = globalServices.find(s => s.key === serviceKey);
      setFormData(prev => ({
        ...prev,
        services: [...(prev.services || []), serviceKey],
        enabled_services: [...(prev.enabled_services || []), { key: serviceKey, name: serviceInfo?.name || serviceKey, has_amount: true, category: serviceInfo?.category }]
      }));
    }
  };

  const togglePaymentType = (index) => {
    setFormData(prev => ({
      ...prev,
      payment_types: prev.payment_types.map((pt, i) => 
        i === index ? { ...pt, is_active: !pt.is_active } : pt
      )
    }));
  };

  const addCustomPaymentType = () => {
    if (!newPaymentType.trim()) {
      toast.error('Please enter a payment method name');
      return;
    }
    // Check for duplicate
    const isDuplicate = (formData.payment_types || []).some(
      pt => pt.name.toLowerCase() === newPaymentType.trim().toLowerCase()
    );
    if (isDuplicate) {
      toast.error('This payment method already exists');
      return;
    }
    setFormData(prev => ({
      ...prev,
      payment_types: [...(prev.payment_types || []), {
        id: `pt-custom-${Date.now()}`,
        name: newPaymentType.trim(),
        is_active: true
      }]
    }));
    setNewPaymentType('');
    toast.success('Payment method added');
  };

  const addFuelGrade = () => {
    if (!newFuelGrade.name) {
      toast.error('Please enter a grade name');
      return;
    }
    if (!newFuelGrade.selling_price || parseFloat(newFuelGrade.selling_price) <= 0) {
      toast.error('Please enter a selling price in pence per litre');
      return;
    }
    const openingStock = parseFloat(newFuelGrade.opening_stock) || 0;
    setFormData(prev => ({
      ...prev,
      fuel_grades: [...prev.fuel_grades, { 
        ...newFuelGrade, 
        average_price: parseFloat(newFuelGrade.average_price) || 0,
        selling_price: parseFloat(newFuelGrade.selling_price) || 0,
        opening_stock: openingStock,
        current_stock: openingStock,  // Set book stock to opening stock
        id: Date.now().toString()
      }]
    }));
    setNewFuelGrade({ name: '', initial_stock: 0, average_price: '', selling_price: '', opening_stock: '' });
    toast.success('Fuel grade added');
  };

  const removeFuelGrade = (gradeId) => {
    setFormData(prev => ({
      ...prev,
      fuel_grades: prev.fuel_grades.filter(g => g.id !== gradeId)
    }));
  };

  const updateFuelGrade = (gradeId, field, value) => {
    setFormData(prev => ({
      ...prev,
      fuel_grades: prev.fuel_grades.map(g => 
        g.id === gradeId 
          ? { ...g, [field]: field.includes('price') || field.includes('stock') ? parseFloat(value) || 0 : value }
          : g
      )
    }));
  };

  const getStoreTypeBadge = (type) => {
    const styles = {
      convenience_store: 'store-type-convenience_store',
      fuel_station: 'store-type-fuel_station',
      property_rental: 'store-type-property_rental'
    };
    return styles[type] || '';
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">You don't have permission to access this page.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="stores-page">
      {/* Wizard Mode */}
      {wizardOpen ? (
        <StoreSetupWizard 
          onComplete={handleWizardComplete}
          onCancel={() => setWizardOpen(false)}
        />
      ) : (
        <>
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                Store Management
              </h1>
              <p className="text-muted-foreground mt-1">
                Configure stores, departments, and payment methods
              </p>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                onClick={() => setWizardOpen(true)} 
                data-testid="setup-wizard-btn"
                className="gap-2"
              >
                <Wand2 className="h-4 w-4" />
                Setup Wizard
              </Button>
              <Button onClick={() => handleOpenDialog()} data-testid="add-store-btn">
                <Plus className="h-4 w-4 mr-2" />
                Quick Add
              </Button>
            </div>
          </div>

      {/* Stores Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : stores.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-muted-foreground">
            <StoreIcon className="h-12 w-12 mb-4 opacity-50" />
            <p>No stores found</p>
            <Button variant="link" onClick={() => handleOpenDialog()}>
              Add your first store
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {stores.map(store => (
            <Card key={store.id} className="card-hover" data-testid={`store-card-${store.id}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className={`p-3 rounded-xl ${
                      store.store_type === 'fuel_station' ? 'bg-orange-500/20' :
                      store.store_type === 'property_rental' ? 'bg-purple-500/20' :
                      'bg-blue-500/20'
                    }`}>
                      {store.store_type === 'fuel_station' ? (
                        <Fuel className="h-6 w-6 text-orange-400" />
                      ) : store.store_type === 'property_rental' ? (
                        <Building2 className="h-6 w-6 text-purple-400" />
                      ) : (
                        <StoreIcon className="h-6 w-6 text-blue-400" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold">{store.name}</h3>
                      <Badge className={`text-xs mt-1 ${getStoreTypeBadge(store.store_type)}`}>
                        {store.store_type.replace('_', ' ')}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm text-muted-foreground mb-4">
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4" />
                    <span>{store.location}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>{store.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>{store.phone}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant="outline" className="text-xs">
                    <Package className="h-3 w-3 mr-1" />
                    {store.departments?.length || 0} Departments
                  </Badge>
                  {store.store_type === 'fuel_station' && (
                    <Badge variant="outline" className="text-xs">
                      <Fuel className="h-3 w-3 mr-1" />
                      {store.fuel_grades?.length || 0} Fuel Grades
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleOpenDialog(store)}
                    data-testid={`edit-store-${store.id}`}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(store)}
                    className="text-destructive hover:text-destructive"
                    data-testid={`delete-store-${store.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add/Edit Store Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingStore ? 'Edit Store' : 'Add New Store'}
            </DialogTitle>
            <DialogDescription>
              Configure store details, departments, and settings
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">
            <TabsList className="grid grid-cols-8 w-full flex-shrink-0">
              <TabsTrigger value="info">Info</TabsTrigger>
              <TabsTrigger value="departments">Departments</TabsTrigger>
              <TabsTrigger value="balances">Balances</TabsTrigger>
              <TabsTrigger value="margin">Dept Monitor</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="payments">Payments</TabsTrigger>
              {formData.store_type === 'fuel_station' && (
                <>
                  <TabsTrigger value="fuel">Fuel</TabsTrigger>
                  <TabsTrigger value="fuel-cards">Fuel Cards</TabsTrigger>
                </>
              )}
            </TabsList>

            <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto pr-2">
              <TabsContent value="info" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Parent Company</Label>
                    <Input
                      value={formData.parent_company || ''}
                      onChange={(e) => setFormData({ ...formData, parent_company: e.target.value })}
                      placeholder="e.g., ABC Holdings Ltd"
                      data-testid="parent-company-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Store Name</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      data-testid="store-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Store Type</Label>
                    <Select 
                      value={formData.store_type} 
                      onValueChange={(value) => setFormData({ ...formData, store_type: value, till_system: '' })}
                    >
                      <SelectTrigger data-testid="store-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {STORE_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Location</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      required
                      data-testid="store-location-input"
                    />
                  </div>
                </div>

                {['fuel_station', 'convenience_store'].includes(formData.store_type) && (
                  <div className="space-y-2">
                    <Label>Till System</Label>
                    <Select
                      value={formData.till_system || 'none'}
                      onValueChange={(value) => setFormData({ ...formData, till_system: value === 'none' ? '' : value })}
                    >
                      <SelectTrigger data-testid="store-till-system-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Not set</SelectItem>
                        {(formData.store_type === 'fuel_station' ? TILL_SYSTEMS.slice(0, 1) : TILL_SYSTEMS.slice(1)).map(t => (
                          <SelectItem key={t.value} value={t.value} data-testid={`till-system-${t.value}`}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground">Day End reports from a different till are rejected with a clear message.</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      data-testid="store-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Email (Optional)</Label>
                    <Input
                      type="email"
                      value={formData.email_secondary}
                      onChange={(e) => setFormData({ ...formData, email_secondary: e.target.value })}
                      placeholder="Secondary notification email"
                      data-testid="store-email-secondary-input"
                    />
                    <p className="text-xs text-muted-foreground">Notifications will be sent to both primary and secondary email</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone</Label>
                    <Input
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      required
                      data-testid="store-phone-input"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="departments" className="space-y-4 mt-4">
                {/* Total Sales (Z-Reading) - Always Required */}
                <div className="border border-primary/30 bg-primary/5 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-primary">Total Sales (Z-Reading)</h4>
                  <p className="text-sm text-muted-foreground">
                    This field is required for all stores. Enter the total from till Z-reading during daily sales entry.
                  </p>
                  <div className="p-3 rounded-lg border border-primary bg-primary/10">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-primary" />
                      <span className="font-medium">Total Sales (Z-Reading)</span>
                      <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded">Required</span>
                    </div>
                  </div>
                </div>

                {/* Other Departments Toggle */}
                <div className="border border-border rounded-lg p-4 space-y-3">
                  <h4 className="font-medium">Optional Settings</h4>
                  <div className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    formData.enable_other_departments ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'
                  }`}>
                    <div>
                      <span className={formData.enable_other_departments ? 'font-medium' : 'text-muted-foreground'}>Other Departments</span>
                      <p className="text-xs text-muted-foreground">Auto-calculated difference (Total Sales - Named Departments)</p>
                    </div>
                    <Switch 
                      checked={formData.enable_other_departments || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, enable_other_departments: checked })}
                    />
                  </div>
                </div>

                <div className="border border-border rounded-lg p-4 space-y-4">
                  <h4 className="font-medium">Add Department</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Department Name"
                      value={newDept.name}
                      onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                      data-testid="dept-name-input"
                    />
                    <Select 
                      value={newDept.field_type} 
                      onValueChange={(value) => setNewDept({ ...newDept, field_type: value })}
                    >
                      <SelectTrigger data-testid="dept-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="amount">Amount (£)</SelectItem>
                        <SelectItem value="volume">Volume (Qty)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" variant="outline" onClick={addDepartment} data-testid="add-dept-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Department
                  </Button>
                  <p className="text-xs text-muted-foreground">
                    Note: Lottery, Scratch Cards, and other services are configured in the Services tab
                  </p>
                </div>

                {formData.departments.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium">Departments</h4>
                    {formData.departments.map((dept, index) => (
                      <div key={dept.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {editingDeptId === dept.id ? (
                            <Input
                              autoFocus
                              value={editingDeptName}
                              onChange={(e) => setEditingDeptName(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') { e.preventDefault(); saveDepartmentName(dept.id); }
                                if (e.key === 'Escape') setEditingDeptId(null);
                              }}
                              className="h-8 w-56"
                              data-testid={`dept-name-input-${dept.id}`}
                            />
                          ) : (
                            <span className="font-medium" data-testid={`dept-name-${dept.id}`}>{dept.name}</span>
                          )}
                          <Badge variant="outline">{dept.field_type}</Badge>
                          {dept.name === 'Fuel Sales' && (
                            <Badge variant="secondary" className="text-xs">Required</Badge>
                          )}
                        </div>
                        {dept.name !== 'Fuel Sales' && (
                          <div className="flex items-center gap-1">
                            {editingDeptId === dept.id ? (
                              <>
                                <Button type="button" variant="ghost" size="icon" onClick={() => saveDepartmentName(dept.id)} data-testid={`dept-name-save-${dept.id}`}>
                                  <Check className="h-4 w-4 text-green-500" />
                                </Button>
                                <Button type="button" variant="ghost" size="icon" onClick={() => setEditingDeptId(null)} data-testid={`dept-name-cancel-${dept.id}`}>
                                  <X className="h-4 w-4" />
                                </Button>
                              </>
                            ) : (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                onClick={() => { setEditingDeptId(dept.id); setEditingDeptName(dept.name); }}
                                data-testid={`dept-edit-btn-${dept.id}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              onClick={() => removeDepartment(dept.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Coffee Group - Compact */}
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-lg p-4 space-y-3">
                  <h4 className="font-medium text-amber-600 flex items-center gap-2">
                    <Coffee className="h-4 w-4" />
                    Coffee Tracking
                  </h4>
                  
                  {/* Coffee Sold Toggle */}
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={formData.coffee_tracking?.coffee_sold_enabled || false}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        coffee_tracking: { ...formData.coffee_tracking, coffee_sold_enabled: checked } 
                      })}
                    />
                    <span className="text-sm">Coffee Sold (collect POS qty)</span>
                  </div>

                  {/* Small Coffee Tracking */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <Switch
                      checked={formData.coffee_tracking?.small_enabled || false}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        coffee_tracking: { ...formData.coffee_tracking, small_enabled: checked } 
                      })}
                    />
                    <span className="text-sm w-28">Small Tracking</span>
                    {formData.coffee_tracking?.small_enabled && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Open:</span>
                        <Input
                          type="number"
                          value={formData.coffee_tracking?.small_opening_reading || 0}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            coffee_tracking: { ...formData.coffee_tracking, small_opening_reading: parseInt(e.target.value) || 0 } 
                          })}
                          className="w-24 h-7 text-xs"
                        />
                      </div>
                    )}
                  </div>

                  {/* Large Coffee Tracking */}
                  <div className="flex items-center gap-3 flex-wrap">
                    <Switch
                      checked={formData.coffee_tracking?.large_enabled || false}
                      onCheckedChange={(checked) => setFormData({ 
                        ...formData, 
                        coffee_tracking: { ...formData.coffee_tracking, large_enabled: checked } 
                      })}
                    />
                    <span className="text-sm w-28">Large Tracking</span>
                    {formData.coffee_tracking?.large_enabled && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">Open:</span>
                        <Input
                          type="number"
                          value={formData.coffee_tracking?.large_opening_reading || 0}
                          onChange={(e) => setFormData({ 
                            ...formData, 
                            coffee_tracking: { ...formData.coffee_tracking, large_opening_reading: parseInt(e.target.value) || 0 } 
                          })}
                          className="w-24 h-7 text-xs"
                        />
                      </div>
                    )}
                  </div>

                  {(formData.coffee_tracking?.coffee_sold_enabled || formData.coffee_tracking?.small_enabled || formData.coffee_tracking?.large_enabled) && (
                    <p className="text-xs text-amber-600 mt-2">
                      Coffee section will appear in Shop Sales tab.
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* Balances & Banking Tab */}
              <TabsContent value="balances" className="space-y-4 mt-4">
                {/* Opening Balances Section */}
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Wallet className="h-4 w-4" />
                        Opening Balances
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Set initial balances for the store. {formData.balances_locked && <span className="text-amber-500">(Locked - Super user can edit)</span>}
                      </p>
                    </div>
                    {editingStore && formData.balances_locked && (
                      <Badge variant="outline" className="text-amber-500 border-amber-500">Locked</Badge>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-sm">Cash Holding Balance (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.opening_balances?.cash_holding || 0}
                        onChange={(e) => setFormData({
                          ...formData,
                          opening_balances: { ...formData.opening_balances, cash_holding: parseFloat(e.target.value) || 0 }
                        })}
                        disabled={formData.balances_locked}
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm">Safe Balance (£)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.opening_balances?.safe || 0}
                        onChange={(e) => setFormData({
                          ...formData,
                          opening_balances: { ...formData.opening_balances, safe: parseFloat(e.target.value) || 0 }
                        })}
                        disabled={formData.balances_locked}
                        className="font-mono"
                      />
                    </div>
                  </div>
                  
                  {!formData.balances_locked && editingStore && (
                    <div className="flex items-center gap-2 pt-2 border-t">
                      <Switch
                        checked={formData.balances_locked}
                        onCheckedChange={(checked) => setFormData({ ...formData, balances_locked: checked })}
                      />
                      <span className="text-sm text-muted-foreground">Lock opening balances after setup</span>
                    </div>
                  )}
                </div>

                {/* Bank Accounts Section */}
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Bank Accounts
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Add multiple bank accounts for the store
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({
                        ...formData,
                        bank_accounts: [...(formData.bank_accounts || []), { id: `bank-${Date.now()}`, name: '', balance: 0 }]
                      })}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Bank
                    </Button>
                  </div>
                  
                  {formData.bank_accounts?.length > 0 ? (
                    <div className="space-y-3">
                      {formData.bank_accounts.map((bank, idx) => (
                        <div key={bank.id || idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="flex-1">
                            <Input
                              placeholder="Bank Name (e.g., Barclays Business)"
                              value={bank.name}
                              onChange={(e) => {
                                const updated = [...formData.bank_accounts];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setFormData({ ...formData, bank_accounts: updated });
                              }}
                              className="mb-2"
                            />
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">Balance: £</span>
                              <Input
                                type="number"
                                step="0.01"
                                placeholder="0.00"
                                value={bank.balance || 0}
                                onChange={(e) => {
                                  const updated = [...formData.bank_accounts];
                                  updated[idx] = { ...updated[idx], balance: parseFloat(e.target.value) || 0 };
                                  setFormData({ ...formData, bank_accounts: updated });
                                }}
                                className="w-32 font-mono"
                              />
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const updated = formData.bank_accounts.filter((_, i) => i !== idx);
                              setFormData({ ...formData, bank_accounts: updated });
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No bank accounts added</p>
                  )}
                </div>

                {/* Credit Cards Section */}
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <CreditCard className="h-4 w-4" />
                        Credit Cards
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Add credit cards with balance and limit tracking
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData({
                        ...formData,
                        credit_cards: [...(formData.credit_cards || []), { id: `cc-${Date.now()}`, name: '', balance: 0, credit_limit: 0 }]
                      })}
                    >
                      <Plus className="h-4 w-4 mr-1" /> Add Card
                    </Button>
                  </div>
                  
                  {formData.credit_cards?.length > 0 ? (
                    <div className="space-y-3">
                      {formData.credit_cards.map((card, idx) => (
                        <div key={card.id || idx} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                          <div className="flex-1">
                            <Input
                              placeholder="Card Name (e.g., Capital On Tap)"
                              value={card.name}
                              onChange={(e) => {
                                const updated = [...formData.credit_cards];
                                updated[idx] = { ...updated[idx], name: e.target.value };
                                setFormData({ ...formData, credit_cards: updated });
                              }}
                              className="mb-2"
                            />
                            <div className="flex items-center gap-4">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Balance: £</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={card.balance || 0}
                                  onChange={(e) => {
                                    const updated = [...formData.credit_cards];
                                    updated[idx] = { ...updated[idx], balance: parseFloat(e.target.value) || 0 };
                                    setFormData({ ...formData, credit_cards: updated });
                                  }}
                                  className="w-28 font-mono"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-muted-foreground">Limit: £</span>
                                <Input
                                  type="number"
                                  step="0.01"
                                  placeholder="0.00"
                                  value={card.credit_limit || 0}
                                  onChange={(e) => {
                                    const updated = [...formData.credit_cards];
                                    updated[idx] = { ...updated[idx], credit_limit: parseFloat(e.target.value) || 0 };
                                    setFormData({ ...formData, credit_cards: updated });
                                  }}
                                  className="w-28 font-mono"
                                />
                              </div>
                            </div>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              const updated = formData.credit_cards.filter((_, i) => i !== idx);
                              setFormData({ ...formData, credit_cards: updated });
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground text-center py-4">No credit cards added</p>
                  )}
                </div>

                {/* Payment Routing Section */}
                {formData.bank_accounts?.length > 0 && (
                  <div className="border border-border rounded-lg p-4 space-y-4">
                    <div>
                      <h4 className="font-medium flex items-center gap-2">
                        <ArrowRight className="h-4 w-4" />
                        Payment Routing
                      </h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Select which bank account receives each payment type
                      </p>
                    </div>
                    
                    {formData.store_type === 'convenience_store' && (
                      <div className="space-y-2">
                        <Label className="text-sm">Card Payments → Bank Account</Label>
                        <Select
                          value={formData.payment_routing?.card_payments || ''}
                          onValueChange={(value) => setFormData({
                            ...formData,
                            payment_routing: { ...formData.payment_routing, card_payments: value }
                          })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select bank account" />
                          </SelectTrigger>
                          <SelectContent>
                            {formData.bank_accounts.map(bank => (
                              <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    
                    {formData.store_type === 'fuel_station' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label className="text-sm">Card Payments (BMS) →</Label>
                          <Select
                            value={formData.payment_routing?.card_payments_bms || ''}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              payment_routing: { ...formData.payment_routing, card_payments_bms: value }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select bank" />
                            </SelectTrigger>
                            <SelectContent>
                              {formData.bank_accounts.map(bank => (
                                <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Bunkering →</Label>
                          <Select
                            value={formData.payment_routing?.bunkering || ''}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              payment_routing: { ...formData.payment_routing, bunkering: value }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select bank" />
                            </SelectTrigger>
                            <SelectContent>
                              {formData.bank_accounts.map(bank => (
                                <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Non-Bunkering →</Label>
                          <Select
                            value={formData.payment_routing?.non_bunkering || ''}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              payment_routing: { ...formData.payment_routing, non_bunkering: value }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select bank" />
                            </SelectTrigger>
                            <SelectContent>
                              {formData.bank_accounts.map(bank => (
                                <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="space-y-2">
                          <Label className="text-sm">Key Fuels →</Label>
                          <Select
                            value={formData.payment_routing?.key_fuels || ''}
                            onValueChange={(value) => setFormData({
                              ...formData,
                              payment_routing: { ...formData.payment_routing, key_fuels: value }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Select bank" />
                            </SelectTrigger>
                            <SelectContent>
                              {formData.bank_accounts.map(bank => (
                                <SelectItem key={bank.id} value={bank.id}>{bank.name}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              {/* Margin Monitoring Tab */}
              <TabsContent value="margin" className="space-y-4 mt-4">
                <div className="border border-border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-medium">Purchase Department Monitoring</h4>
                      <p className="text-xs text-muted-foreground mt-1">
                        Track purchase costs by department for margin analysis (Shop purchases only)
                      </p>
                    </div>
                    <Switch 
                      checked={formData.department_monitoring_enabled || false}
                      onCheckedChange={(checked) => {
                        setFormData({ 
                          ...formData, 
                          department_monitoring_enabled: checked,
                          monitored_departments: checked ? formData.monitored_departments : []
                        });
                      }}
                    />
                  </div>

                  {formData.department_monitoring_enabled && (
                    <div className="space-y-4 pt-3 border-t border-border">
                      {/* Department Selection with VAT Rate */}
                      <div className="space-y-2">
                        <Label>Select Departments to Monitor</Label>
                        <p className="text-xs text-muted-foreground">
                          Choose departments to track in Shop purchase invoices. Set VAT rate for each. "Other" will be auto-added.
                        </p>
                        <div className="space-y-2 mt-3">
                          {formData.departments.map((dept) => {
                            const monitoredDept = (formData.monitored_departments || []).find(
                              m => m.department_id === dept.id
                            );
                            const isMonitored = !!monitoredDept;
                            return (
                              <div 
                                key={dept.id}
                                className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                                  isMonitored ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                                }`}
                              >
                                <div 
                                  className="flex items-center gap-2 cursor-pointer flex-1"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isMonitored) {
                                      setFormData({
                                        ...formData,
                                        monitored_departments: formData.monitored_departments.filter(
                                          m => m.department_id !== dept.id
                                        )
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        monitored_departments: [
                                          ...(formData.monitored_departments || []),
                                          { department_id: dept.id, department_name: dept.name, vat_rate: 20 }
                                        ]
                                      });
                                    }
                                  }}
                                >
                                  <div className={`h-4 w-4 rounded-sm border flex items-center justify-center ${
                                    isMonitored ? 'bg-primary border-primary' : 'border-muted-foreground'
                                  }`}>
                                    {isMonitored && <CheckCircle className="h-3 w-3 text-primary-foreground" />}
                                  </div>
                                  <span className={isMonitored ? 'font-medium' : ''}>{dept.name}</span>
                                </div>
                                {isMonitored && (
                                  <Select 
                                    value={String(monitoredDept?.vat_rate ?? 20)} 
                                    onValueChange={(value) => {
                                      setFormData({
                                        ...formData,
                                        monitored_departments: formData.monitored_departments.map(m =>
                                          m.department_id === dept.id 
                                            ? { ...m, vat_rate: Number(value) }
                                            : m
                                        )
                                      });
                                    }}
                                  >
                                    <SelectTrigger className="w-[120px]" onClick={(e) => e.stopPropagation()}>
                                      <SelectValue placeholder="VAT Rate" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="20">20% VAT</SelectItem>
                                      <SelectItem value="5">5% VAT</SelectItem>
                                      <SelectItem value="0">0% VAT</SelectItem>
                                    </SelectContent>
                                  </Select>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        {formData.departments.length === 0 && (
                          <p className="text-xs text-amber-500">Add departments in the Departments tab first</p>
                        )}
                      </div>

                      {/* "Other" Department Note */}
                      {(formData.monitored_departments || []).length > 0 && (
                        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                          <p className="text-sm text-blue-400">
                            <strong>Note:</strong> "Other" department will be automatically added. Its VAT is calculated as: Invoice VAT - Sum of Department VATs.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Services Tab */}
              <TabsContent value="services" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Lottery & Sales Services</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {globalServices.filter(s => s.category === 'lottery').map((service) => {
                      const isEnabled = (formData.services || []).includes(service.key);
                      return (
                        <div 
                          key={service.key}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isEnabled ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'
                          }`}
                        >
                          <span className={isEnabled ? 'font-medium' : 'text-muted-foreground'}>{service.name}</span>
                          <Switch 
                            checked={isEnabled} 
                            onCheckedChange={() => toggleService(service.key)} 
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Payment & Money Transfer Services</h4>
                  <div className="grid grid-cols-2 gap-3">
                    {globalServices.filter(s => s.category === 'services').map((service) => {
                      const isEnabled = (formData.services || []).includes(service.key);
                      return (
                        <div 
                          key={service.key}
                          className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                            isEnabled ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'
                          }`}
                        >
                          <span className={isEnabled ? 'font-medium' : 'text-muted-foreground'}>{service.name}</span>
                          <Switch 
                            checked={isEnabled} 
                            onCheckedChange={() => toggleService(service.key)} 
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>

                {formData.store_type === 'fuel_station' && (
                  <div className="space-y-4">
                    <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wider">Fuel Station Services</h4>
                    <div className="grid grid-cols-2 gap-3">
                      {globalServices.filter(s => s.category === 'fuel_services').map((service) => {
                        const isEnabled = (formData.services || []).includes(service.key);
                        return (
                          <div 
                            key={service.key}
                            className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                              isEnabled ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'
                            }`}
                          >
                            <span className={isEnabled ? 'font-medium' : 'text-muted-foreground'}>{service.name}</span>
                            <Switch 
                              checked={isEnabled} 
                              onCheckedChange={() => toggleService(service.key)} 
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                
                {/* Creditor Request Feature */}
                <div className="space-y-3 border-t pt-4">
                  <h4 className="font-semibold text-blue-500 flex items-center gap-2">
                    <Landmark className="h-4 w-4" />
                    Creditor Requests
                    <span className="text-xs text-muted-foreground font-normal">(Credit & Repayment tracking)</span>
                  </h4>
                  <div className="flex items-center justify-between p-3 rounded-lg border">
                    <div className="space-y-0.5">
                      <Label className="text-sm font-medium">Enable Creditor Requests</Label>
                      <p className="text-xs text-muted-foreground">
                        Allow creditors to submit credit and repayment requests for reconciliation
                      </p>
                    </div>
                    <Switch
                      checked={formData.creditor_request_enabled || false}
                      onCheckedChange={(checked) => setFormData({ ...formData, creditor_request_enabled: checked })}
                      data-testid="creditor-request-toggle"
                    />
                  </div>
                  {formData.creditor_request_enabled && (
                    <p className="text-xs text-blue-400 bg-blue-500/10 p-2 rounded">
                      Creditor users assigned to this store can submit credit and repayment requests.
                    </p>
                  )}
                </div>
              </TabsContent>

              {/* Payments Tab */}
              <TabsContent value="payments" className="space-y-4 mt-4">
                <div className="space-y-4">
                  <h4 className="font-medium">Payment Methods</h4>
                  <p className="text-sm text-muted-foreground">Enable or disable payment methods for this store</p>
                  <div className="grid grid-cols-2 gap-3">
                    {(formData.payment_types || []).map((pt, index) => (
                      <div 
                        key={pt.id || index}
                        className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                          pt.is_active ? 'border-primary bg-primary/10' : 'border-border bg-muted/30'
                        }`}
                      >
                        <span className={pt.is_active ? 'font-medium' : 'text-muted-foreground'}>{pt.name}</span>
                        <Switch 
                          checked={pt.is_active} 
                          onCheckedChange={() => togglePaymentType(index)} 
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Add Custom Payment Type */}
                <div className="border border-border rounded-lg p-4 space-y-3">
                  <h4 className="font-medium">Add Custom Payment Method</h4>
                  <div className="flex gap-3">
                    <Input
                      placeholder="Payment method name (e.g., Bank Transfer)"
                      value={newPaymentType}
                      onChange={(e) => setNewPaymentType(e.target.value)}
                      className="flex-1"
                      data-testid="custom-payment-input"
                    />
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={addCustomPaymentType}
                      data-testid="add-custom-payment-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add
                    </Button>
                  </div>
                </div>
              </TabsContent>

              {formData.store_type === 'fuel_station' && (
                <>
                <TabsContent value="fuel" className="space-y-4 mt-4">
                  <div className="border border-border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">Add Fuel Grade</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="space-y-2">
                        <Label>Grade Name *</Label>
                        <Input
                          placeholder="e.g., Diesel, Unleaded"
                          value={newFuelGrade.name}
                          onChange={(e) => setNewFuelGrade({ ...newFuelGrade, name: e.target.value })}
                          data-testid="fuel-grade-name-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Average Cost (p/L) *</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g., 132.500"
                            value={newFuelGrade.average_price || ''}
                            onChange={(e) => {
                              if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                                setNewFuelGrade({ ...newFuelGrade, average_price: e.target.value });
                              }
                            }}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setNewFuelGrade({ ...newFuelGrade, average_price: val });
                            }}
                            className="pr-10"
                            data-testid="fuel-avg-price-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">p/L</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Cost price in pence</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Selling Price (p/L) *</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g., 144.9"
                            value={newFuelGrade.selling_price || ''}
                            onChange={(e) => {
                              if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                                setNewFuelGrade({ ...newFuelGrade, selling_price: e.target.value });
                              }
                            }}
                            onBlur={(e) => {
                              const val = parseFloat(e.target.value) || 0;
                              setNewFuelGrade({ ...newFuelGrade, selling_price: val });
                            }}
                            className="pr-10"
                            data-testid="fuel-sell-price-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">p/L</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Must end with .9</p>
                      </div>
                      <div className="space-y-2">
                        <Label>Opening Stock (Litres)</Label>
                        <div className="relative">
                          <Input
                            type="text"
                            inputMode="decimal"
                            placeholder="e.g., 5000"
                            value={newFuelGrade.opening_stock || ''}
                            onChange={(e) => {
                              if (e.target.value === '' || /^[0-9]*\.?[0-9]*$/.test(e.target.value)) {
                                setNewFuelGrade({ ...newFuelGrade, opening_stock: e.target.value });
                              }
                            }}
                            className="pr-6"
                            data-testid="fuel-opening-stock-input"
                          />
                          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">L</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Initial tank stock</p>
                      </div>
                    </div>
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={addFuelGrade}
                      disabled={!newFuelGrade.name || !newFuelGrade.selling_price}
                      data-testid="add-fuel-grade-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Fuel Grade
                    </Button>
                    <p className="text-xs text-muted-foreground">
                      All prices are in pence (e.g., 144.9p = £1.449). Opening stock sets the initial book stock.
                    </p>
                  </div>

                  {formData.fuel_grades.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium">Configured Fuel Grades</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full">
                          <thead className="bg-muted/50">
                            <tr>
                              <th className="text-left p-3 text-sm font-medium">Grade Name</th>
                              <th className="text-center p-3 text-sm font-medium">Cost (p/L)</th>
                              <th className="text-center p-3 text-sm font-medium">Selling (p/L)</th>
                              <th className="text-center p-3 text-sm font-medium">Opening Stock (L)</th>
                              <th className="text-center p-3 text-sm font-medium w-16">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {formData.fuel_grades.map((grade) => {
                              const gradeColors = getFuelGradeColor(grade.name);
                              return (
                              <tr key={grade.id} className="hover:bg-muted/30">
                                <td className="p-2">
                                  <div className="flex items-center gap-2">
                                    <span className={`w-3 h-3 rounded-full flex-shrink-0 ${gradeColors.bg}`}></span>
                                    <Input
                                      value={grade.name}
                                      onChange={(e) => updateFuelGrade(grade.id, 'name', e.target.value)}
                                      className="h-9"
                                    />
                                  </div>
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    step="0.001"
                                    value={grade.average_price || ''}
                                    onChange={(e) => updateFuelGrade(grade.id, 'average_price', e.target.value)}
                                    className="h-9 text-center font-mono"
                                    placeholder="0.000"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    step="0.1"
                                    value={grade.selling_price || ''}
                                    onChange={(e) => updateFuelGrade(grade.id, 'selling_price', e.target.value)}
                                    className="h-9 text-center font-mono"
                                    placeholder="0.0"
                                  />
                                </td>
                                <td className="p-2">
                                  <Input
                                    type="number"
                                    value={grade.opening_stock || grade.current_stock || ''}
                                    onChange={(e) => {
                                      updateFuelGrade(grade.id, 'opening_stock', e.target.value);
                                      updateFuelGrade(grade.id, 'current_stock', e.target.value);
                                    }}
                                    className="h-9 text-center font-mono"
                                    placeholder="0"
                                  />
                                </td>
                                <td className="p-2 text-center">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => removeFuelGrade(grade.id)}
                                    className="h-9 w-9"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {formData.fuel_grades.length === 0 && (
                    <div className="text-center py-6 text-muted-foreground border border-dashed rounded-lg">
                      <Fuel className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No fuel grades configured yet.</p>
                      <p className="text-sm">Add fuel grades above with their selling and cost prices.</p>
                    </div>
                  )}
                </TabsContent>

                {/* Fuel Cards Tab */}
                <TabsContent value="fuel-cards" className="space-y-4 mt-4">
                  <div className="space-y-4">
                    <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-sm text-blue-400">
                        <CreditCard className="h-4 w-4 inline mr-2" />
                        Select the fuel cards accepted at this station
                      </p>
                    </div>

                    <div className="space-y-6">
                      {/* Non Bunkering Cards */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-blue-500 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Non Bunkering Cards
                          <span className="text-xs text-muted-foreground font-normal">(Retail price - Weekly)</span>
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {globalFuelCardTypes.filter(fc => fc.category === 'non_bunkering').map((fc) => (
                            <label key={fc.id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-muted/50">
                              <input
                                type="checkbox"
                                  checked={formData.active_fuel_cards?.some(afc => afc.card_type_id === fc.id || afc.card_name === fc.name)}
                                  onChange={(e) => {
                                    // Remove any existing card with same name or ID first to prevent duplicates
                                    const filteredCards = (formData.active_fuel_cards || []).filter(
                                      afc => afc.card_type_id !== fc.id && afc.card_name !== fc.name
                                    );
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        active_fuel_cards: [...filteredCards, {
                                          card_type_id: fc.id,
                                          card_name: fc.name,
                                          category: fc.category
                                        }]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        active_fuel_cards: filteredCards
                                      });
                                    }
                                  }}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <span className="text-sm font-medium">{fc.name}</span>
                              </label>
                            ))}
                          </div>
                          {globalFuelCardTypes.filter(fc => fc.category === 'non_bunkering').length === 0 && (
                            <p className="text-sm text-muted-foreground">No non-bunkering cards configured in Settings.</p>
                          )}
                        </div>

                      {/* Bunkering Cards */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-orange-500 flex items-center gap-2">
                          <Fuel className="h-4 w-4" />
                          Bunkering Cards
                          <span className="text-xs text-muted-foreground font-normal">(Cost + Fee - Weekly)</span>
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {globalFuelCardTypes.filter(fc => fc.category === 'bunkering').map((fc) => (
                            <label key={fc.id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-muted/50">
                              <input
                                type="checkbox"
                                checked={formData.active_fuel_cards?.some(afc => afc.card_type_id === fc.id || afc.card_name === fc.name)}
                                  onChange={(e) => {
                                    // Remove any existing card with same name or ID first to prevent duplicates
                                    const filteredCards = (formData.active_fuel_cards || []).filter(
                                      afc => afc.card_type_id !== fc.id && afc.card_name !== fc.name
                                    );
                                    if (e.target.checked) {
                                      setFormData({
                                        ...formData,
                                        active_fuel_cards: [...filteredCards, {
                                          card_type_id: fc.id,
                                          card_name: fc.name,
                                          category: fc.category,
                                          handling_fee_per_litre: fc.handling_fee_per_litre
                                        }]
                                      });
                                    } else {
                                      setFormData({
                                        ...formData,
                                        active_fuel_cards: filteredCards
                                      });
                                    }
                                  }}
                                  className="h-4 w-4 rounded border-gray-300"
                                />
                                <span className="text-sm font-medium">{fc.name}</span>
                                <span className="text-xs text-muted-foreground">({fc.handling_fee_per_litre}p/L)</span>
                              </label>
                            ))}
                          </div>
                          {globalFuelCardTypes.filter(fc => fc.category === 'bunkering').length === 0 && (
                            <p className="text-sm text-muted-foreground">No bunkering cards configured in Settings.</p>
                          )}
                        </div>

                      {/* Key Fuels */}
                      <div className="space-y-3">
                        <h4 className="font-semibold text-green-500 flex items-center gap-2">
                          <Fuel className="h-4 w-4" />
                          Key Fuels
                        <span className="text-xs text-muted-foreground font-normal">(Diesel: Cost + Fee | Others: Retail - % - Monthly)</span>
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {globalFuelCardTypes.filter(fc => fc.category === 'key_fuels').map((fc) => (
                            <label key={fc.id} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-muted/50">
                              <input
                                type="checkbox"
                                checked={formData.active_fuel_cards?.some(afc => afc.card_type_id === fc.id || afc.card_name === fc.name)}
                                onChange={(e) => {
                                  // Remove any existing card with same name or ID first to prevent duplicates
                                  const filteredCards = (formData.active_fuel_cards || []).filter(
                                    afc => afc.card_type_id !== fc.id && afc.card_name !== fc.name
                                  );
                                  if (e.target.checked) {
                                    setFormData({
                                      ...formData,
                                      active_fuel_cards: [...filteredCards, {
                                        card_type_id: fc.id,
                                        card_name: fc.name,
                                        category: fc.category,
                                        diesel_handling_fee_per_litre: fc.diesel_handling_fee_per_litre,
                                        other_grades_merchant_fee_percent: fc.other_grades_merchant_fee_percent,
                                        shop_items_merchant_fee_percent: fc.shop_items_merchant_fee_percent
                                      }]
                                    });
                                  } else {
                                    setFormData({
                                      ...formData,
                                      active_fuel_cards: filteredCards
                                    });
                                  }
                                }}
                                className="h-4 w-4 rounded border-gray-300"
                              />
                              <span className="text-sm font-medium">{fc.name}</span>
                            </label>
                          ))}
                        </div>
                        {globalFuelCardTypes.filter(fc => fc.category === 'key_fuels').length === 0 && (
                          <p className="text-sm text-muted-foreground">No key fuels cards configured in Settings.</p>
                        )}
                      </div>

                      {/* Summary of selected cards */}
                      {formData.active_fuel_cards?.length > 0 && (
                        <div className="p-3 bg-muted/30 rounded-lg">
                          <p className="text-sm font-medium mb-2">Selected Cards ({[...new Set(formData.active_fuel_cards.map(c => c.card_name))].length}):</p>
                          <div className="flex flex-wrap gap-2">
                            {[...new Map(formData.active_fuel_cards.map(c => [c.card_name, c])).values()].map((afc, idx) => (
                              <Badge key={idx} variant="secondary" className="text-xs">
                                {afc.card_name}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* AMEX Card Tracking */}
                      <div className="space-y-3 border-t pt-4">
                        <h4 className="font-semibold text-purple-500 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          AMEX Card Tracking
                          <span className="text-xs text-muted-foreground font-normal">(Weekly settlement)</span>
                        </h4>
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-0.5">
                            <Label className="text-sm font-medium">Enable AMEX Tracking</Label>
                            <p className="text-xs text-muted-foreground">
                              Track AMEX payments separately and settle weekly (like fuel cards)
                            </p>
                          </div>
                          <Switch
                            checked={formData.amex_tracking_enabled || false}
                            onCheckedChange={(checked) => setFormData({ ...formData, amex_tracking_enabled: checked })}
                          />
                        </div>
                        {formData.amex_tracking_enabled && (
                          <p className="text-xs text-purple-400 bg-purple-500/10 p-2 rounded">
                            AMEX payments will be tracked separately and added to bank balance only when settled.
                          </p>
                        )}
                      </div>

                      {/* Voucher Tracking */}
                      <div className="space-y-3 border-t pt-4">
                        <h4 className="font-semibold text-emerald-500 flex items-center gap-2">
                          <CreditCard className="h-4 w-4" />
                          Voucher Tracking
                          <span className="text-xs text-muted-foreground font-normal">(Settlement tracking)</span>
                        </h4>
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="space-y-0.5">
                            <Label className="text-sm font-medium">Enable Voucher Tracking</Label>
                            <p className="text-xs text-muted-foreground">
                              Track voucher payments separately and settle when redeemed
                            </p>
                          </div>
                          <Switch
                            checked={formData.voucher_tracking_enabled || false}
                            onCheckedChange={(checked) => setFormData({ ...formData, voucher_tracking_enabled: checked })}
                          />
                        </div>
                        {formData.voucher_tracking_enabled && (
                          <p className="text-xs text-emerald-400 bg-emerald-500/10 p-2 rounded">
                            Voucher payments will be tracked separately and added to bank balance only when settled.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </TabsContent>
                </>
              )}
              </div>

              <DialogFooter className="mt-4 flex-shrink-0 border-t pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={saving} data-testid="save-store-btn">
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    editingStore ? 'Update Store' : 'Create Store'
                  )}
                </Button>
              </DialogFooter>
            </form>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Store Delete Verification Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
        if (!open) {
          setDeleteDialogOpen(false);
          setStoreToDelete(null);
          setDeleteStep('confirm');
          setVerificationCode('');
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <ShieldAlert className="h-5 w-5" />
              {deleteStep === 'confirm' ? 'Confirm Store Deletion' : 'Enter Verification Code'}
            </DialogTitle>
            <DialogDescription>
              {deleteStep === 'confirm' 
                ? 'This action will permanently delete the store and ALL associated data.'
                : 'Enter the 6-digit code sent to your email to confirm deletion.'
              }
            </DialogDescription>
          </DialogHeader>

          {deleteStep === 'confirm' ? (
            <div className="space-y-4">
              <div className="p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-destructive mt-0.5" />
                  <div>
                    <p className="font-medium text-destructive">
                      You are about to delete: {storeToDelete?.name}
                    </p>
                    <p className="text-sm text-muted-foreground mt-2">
                      This will permanently remove:
                    </p>
                    <ul className="text-sm text-muted-foreground list-disc list-inside mt-1">
                      <li>All sales entries</li>
                      <li>All purchases and supplier data</li>
                      <li>All money transfers</li>
                      <li>All scratch card stock and settlements</li>
                      <li>All creditor transactions</li>
                      <li>All other store-related data</li>
                    </ul>
                    <p className="text-sm text-destructive font-medium mt-2">
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleRequestDeleteCode}
                  disabled={deletingStore}
                  data-testid="request-delete-code-btn"
                >
                  {deletingStore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Sending Code...
                    </>
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input
                  id="verification-code"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  className="text-center text-2xl tracking-widest font-mono"
                  data-testid="delete-verification-code"
                />
                <p className="text-xs text-muted-foreground">
                  Code sent to your email. Valid for 15 minutes.
                </p>
              </div>

              <DialogFooter className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={() => setDeleteStep('confirm')}
                >
                  Back
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleConfirmDelete}
                  disabled={deletingStore || verificationCode.length !== 6}
                  data-testid="confirm-delete-btn"
                >
                  {deletingStore ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Deleting...
                    </>
                  ) : (
                    'Permanently Delete Store'
                  )}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
};

export default StoresPage;
